import React, { useState, useEffect } from 'react';
import { ShieldCheck, Plus, RefreshCw, X, Tag } from 'lucide-react';
import Layout from '../Layout';
import { listAdmins, createAdmin, fetchCharaTypes, addCharaType as apiAddCharaType, deleteCharaType as apiDeleteCharaType } from '../../api';

function AdminPage({ userRole, userEmail }) {
  const [form, setForm] = useState({ email:'', password:'', confirmPassword:'' });
  const [admins, setAdmins] = useState([]);
  const [successMsg, setSuccessMsg] = useState('');
  const [errorMsg, setErrorMsg] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingAdmins, setLoadingAdmins] = useState(true);

  /* chara type management */
  const [charaTypes, setCharaTypes] = useState([]);
  const [newChara, setNewChara] = useState('');
  const [charaLoading, setCharaLoading] = useState(false);
  const [charaMsg, setCharaMsg] = useState('');

  const loadAdmins = async () => {
    setLoadingAdmins(true);
    try {
      const { admins: list } = await listAdmins();
      setAdmins(list.map(a => ({
        email: a.email,
        role: a.email === userEmail ? 'Current Admin (You)' : (a.role === 'superadmin' ? 'Super Admin' : 'Admin'),
      })));
    } catch (err) { console.error(err); }
    finally { setLoadingAdmins(false); }
  };

  const loadCharaTypes = async () => {
    const { data } = await fetchCharaTypes();
    if (data) setCharaTypes(data);
  };

  useEffect(() => { loadAdmins(); loadCharaTypes(); }, []);

  const handleSubmit = async (e) => {
    e.preventDefault(); setSuccessMsg(''); setErrorMsg('');
    if (form.password !== form.confirmPassword) { setErrorMsg('Passwords do not match.'); return; }
    if (form.password.length < 6) { setErrorMsg('Password must be at least 6 characters.'); return; }
    setLoading(true);
    try {
      await createAdmin(form.email, form.password);
      setAdmins(prev => [...prev, { email: form.email, role: 'Admin' }]);
      setSuccessMsg(`Admin "${form.email}" created successfully.`);
      setForm({ email:'', password:'', confirmPassword:'' });
    } catch (err) {
      setErrorMsg(err.message || 'Failed to create admin.');
    } finally { setLoading(false); }
  };

  const addCharaType = async () => {
    if (!newChara.trim()) return;
    setCharaLoading(true); setCharaMsg('');
    try {
      await apiAddCharaType(newChara.trim());
      setCharaMsg(`"${newChara.trim()}" added.`); setNewChara(''); loadCharaTypes();
    } catch (err) {
      setCharaMsg('Error: ' + err.message);
    }
    setCharaLoading(false);
  };

  const deleteCharaType = async (id, name) => {
    if (!window.confirm(`Delete "${name}"?`)) return;
    await apiDeleteCharaType(id);
    loadCharaTypes();
  };

  return (
    <Layout userRole={userRole} userEmail={userEmail}
      pageTitle="Admin Management" pageSubtitle="Manage admins and system settings">

      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:20 }}>

        {/* ── Create Admin ── */}
        <div className="section-card">
          <div className="section-header">
            <div className="section-title"><ShieldCheck size={16} />Create New Admin</div>
          </div>
          <div className="section-body">
            {successMsg && (
              <div style={{ background:'#D1FAE5', color:'#065F46', borderRadius:8,
                padding:'10px 14px', fontSize:13, marginBottom:16 }}>{successMsg}</div>
            )}
            {errorMsg && (
              <div style={{ background:'#FEE2E2', color:'#991B1B', borderRadius:8,
                padding:'10px 14px', fontSize:13, marginBottom:16 }}>{errorMsg}</div>
            )}
            <form onSubmit={handleSubmit} className="modern-form">
              <div className="form-group">
                <label>Email Address</label>
                <input type="email" placeholder="newadmin@example.com"
                  value={form.email}
                  onChange={e => setForm(p=>({...p,email:e.target.value}))} required />
              </div>
              <div className="form-group">
                <label>Password</label>
                <input type="password" placeholder="Min 6 characters"
                  value={form.password}
                  onChange={e => setForm(p=>({...p,password:e.target.value}))} required />
              </div>
              <div className="form-group">
                <label>Confirm Password</label>
                <input type="password" placeholder="Re-enter password"
                  value={form.confirmPassword}
                  onChange={e => setForm(p=>({...p,confirmPassword:e.target.value}))} required />
              </div>
              <button type="submit" className="btn-primary-dash" disabled={loading}>
                {loading
                  ? <><span className="spinner-border spinner-border-sm" /> Creating…</>
                  : <><Plus size={15} /> Create Admin</>}
              </button>
            </form>
          </div>
        </div>

        {/* ── Chara Types ── */}
        <div className="section-card">
          <div className="section-header">
            <div className="section-title"><Tag size={16} />Chara Types</div>
            <button className="btn-icon" onClick={loadCharaTypes}><RefreshCw size={13} /></button>
          </div>
          <div className="section-body">
            <div style={{ display:'flex', gap:8, marginBottom:16 }}>
              <input
                type="text"
                placeholder="e.g. Wheat Straw, Bajra…"
                value={newChara}
                onChange={e => setNewChara(e.target.value)}
                onKeyDown={e => e.key==='Enter' && addCharaType()}
                style={{ flex:1, padding:'9px 12px', border:'1.5px solid #E2E8F0',
                  borderRadius:8, fontSize:14, outline:'none' }}
              />
              <button className="btn-primary-dash" onClick={addCharaType} disabled={charaLoading || !newChara.trim()}>
                <Plus size={15} /> Add
              </button>
            </div>
            {charaMsg && (
              <div style={{ fontSize:13, color: charaMsg.startsWith('Error') ? '#991B1B' : '#065F46',
                marginBottom:12 }}>{charaMsg}</div>
            )}
            <div style={{ display:'flex', flexWrap:'wrap', gap:8 }}>
              {charaTypes.length === 0
                ? <span style={{ fontSize:13, color:'#9CA3AF' }}>No chara types added yet.</span>
                : charaTypes.map(t => (
                    <div key={t.id} style={{
                      display:'flex', alignItems:'center', gap:6,
                      background:'#EBF3FF', borderRadius:20,
                      padding:'5px 12px 5px 14px',
                      fontSize:13, color:'#1E5FA8', fontWeight:600,
                    }}>
                      {t.name}
                      <button onClick={() => deleteCharaType(t.id, t.name)}
                        style={{ background:'none', border:'none', cursor:'pointer',
                          color:'#1E5FA8', display:'flex', alignItems:'center',
                          padding:0, marginLeft:2, opacity:.7 }}
                        title="Delete">
                        <X size={12} />
                      </button>
                    </div>
                  ))
              }
            </div>
          </div>
        </div>

        {/* ── Admins List ── */}
        <div className="section-card" style={{ gridColumn:'1/-1' }}>
          <div className="section-header">
            <div className="section-title"><ShieldCheck size={16} />Admin Accounts</div>
            <button className="btn-ghost" onClick={loadAdmins}>
              <RefreshCw size={13} /> Refresh
            </button>
          </div>
          <div className="modern-table-wrap">
            <table className="modern-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Email</th>
                  <th>Role</th>
                </tr>
              </thead>
              <tbody>
                {loadingAdmins ? (
                  <tr>
                    <td colSpan={3} style={{ textAlign:'center', padding:'30px 0', color:'#6B7280' }}>Loading…</td>
                  </tr>
                ) : admins.map((a, i) => (
                  <tr key={i}>
                    <td style={{ color:'#9CA3AF', fontSize:12 }}>{i+1}</td>
                    <td><strong>{a.email}</strong></td>
                    <td>
                      <span className={`badge-pill ${a.role.includes('Current') ? 'danger' : 'primary'}`}>
                        {a.role}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

      </div>
    </Layout>
  );
}

export default AdminPage;
